import { cn } from '../../lib/cn.js'
import { Skeleton } from '../ui/Skeleton.js'
import { Text } from '../ui/Typography.js'

export interface Figure {
  label: React.ReactNode
  value: React.ReactNode
  /** What it is measured against — "12% of what was taken". */
  comparison?: React.ReactNode
  /** Show me the ones this is counting. See `StatCard`, which explains it. */
  onSelect?: () => void
  /** Whether the table is already narrowed to this. */
  selected?: boolean
}

export interface FigureStripProps {
  figures: Figure[]
  loading?: boolean
  className?: string
}

/**
 * The figures over a table, on one line.
 *
 * Said plainly, looking at the running desk: *"the count info on top of all
 * pages are so big yet they give so little info so table should take more
 * screen space and the count info should be so compact"*.
 *
 * Four `StatCard`s took **a hundred and forty pixels** of an 800px window —
 * a fifth of the screen — to say four numbers, and pushed the table's first
 * row below the fold on a laptop. The numbers are worth having; a card each
 * is not. This is the same four in one 56px row.
 *
 * It is the shape the host's console already uses, and for the reason
 * recorded there: *"a console is acted on rather than read, and pushing the
 * tabs a hundred and twenty pixels down the page to make room for cards would
 * cost the host the thing they came for."* A list screen is the same — what
 * somebody came for is the list.
 *
 * `StatCard` stays where a figure is the content rather than the headline
 * over it — the overview's *"this month"* block, which is six numbers next to
 * a chart and is the point of that part of the page. The overview's top four
 * are a headline like any other and are a strip, because the panel under them
 * is the one thing that screen exists for.
 */
export function FigureStrip({ figures, loading, className }: FigureStripProps) {
  return (
    /**
     * Two across on a phone, one line from `sm` up.
     *
     * Four figures sharing 390px is about ninety pixels each, and the labels
     * came out as *"BOOKIN…"*, *"MATCHI…"*, *"SUSPE…"* — the word that says
     * what the number *is*, truncated, leaving four numbers meaning nothing.
     * A 2×2 grid gives each one a readable width and costs one row of height.
     *
     * `divide-x` only applies from `sm`, where they are actually a row:
     * vertical rules through a grid draw a cross.
     */
    <div
      className={cn(
        'border-hairline bg-surface grid grid-cols-2 rounded-md border',
        'divide-hairline divide-x-0 divide-y sm:flex sm:divide-x sm:divide-y-0',
        className,
      )}
    >
      {figures.map((f, i) => (
        <One key={i} figure={f} loading={loading} />
      ))}
    </div>
  )
}

function One({ figure, loading }: { figure: Figure; loading?: boolean }) {
  const { label, value, comparison, onSelect, selected } = figure
  const body = (
    <>
      <Text as="span" variant="eyebrow" className="text-ink-muted truncate">
        {label}
      </Text>
      {loading ? (
        <Skeleton className="h-5 w-16" />
      ) : (
        <span className="flex items-baseline gap-2">
          <Text as="span" variant="title" className="tabular-nums leading-none">
            {value}
          </Text>
          {/* Beside the number rather than under it — the row is one line
              high, and this is the half of the figure that says what it
              means. Hidden on a narrow window, where it is the first thing
              worth losing. */}
          {comparison && (
            <Text as="span" variant="caption" className="text-ink-muted hidden truncate lg:inline">
              {comparison}
            </Text>
          )}
        </span>
      )}
    </>
  )

  const shared = 'flex min-w-0 flex-1 flex-col gap-1 px-4 py-3 text-left'

  if (!onSelect) return <div className={shared}>{body}</div>

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        shared,
        'transition-colors outline-none hover:bg-sunk',
        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-coral-fill/30',
        /* Chosen is marked on the figure, not only on the tab it moves. */
        selected && 'bg-coral-soft',
      )}
    >
      {body}
    </button>
  )
}
